// src/pages/TourStatements/TourStatementsEmployeeList.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import tourStatementService from '../../services/tourStatement.service';
import Toast from '../../components/Toast/Toast';
import './TourStatements.css';

const TourStatementsEmployeeList = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const location = useLocation();
  const companyName = location.state?.companyName || 'Company';

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchEmployeesWithTourStatements();
  }, [companyId]);

  const fetchEmployeesWithTourStatements = async () => {
    setLoading(true);
    try {
      // Fetch tour statements for this company
      const response = await tourStatementService.getTourStatementsByCompany(companyId);

      if (response.success && response.data) {
        // Extract unique users from tour statements
        const statements = response.data;
        
        // Group by user to get unique employees
        const uniqueEmployees = [];
        const seenUserIds = new Set();

        statements.forEach(statement => {
          if (statement.user && !seenUserIds.has(statement.user.id)) {
            seenUserIds.add(statement.user.id);
            uniqueEmployees.push({
              id: statement.user.id,
              fullName: statement.user.fullName || `${statement.user.firstName} ${statement.user.lastName}`,
              firstName: statement.user.firstName,
              lastName: statement.user.lastName,
              company: statement.company,
              role: statement.user.role,
            });
          }
        });

        setEmployees(uniqueEmployees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setToast({ message: 'Failed to fetch employees', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewStatement = (employee) => {
    navigate(`/orders/tour-statements/view/${employee.id}`, {
      state: { 
        employeeName: employee.fullName || `${employee.firstName} ${employee.lastName}`,
        companyId,
        companyName
      }
    });
  };

  const handleBack = () => {
    navigate('/orders/tour-statements');
  };

  return (
    <div className="tour-statements-page">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="tour-header">
        <h4 className="tour-title">Tour Statements</h4>
      </div>

      {/* Back Button */}
      <button className="btn-back" onClick={handleBack}>
        ← Back
      </button>

      {/* Employee List */}
      <div className="employee-list-section">
        <h5 className="section-subtitle">Employees - {companyName}</h5>

        <div className="employees-table-container">
          {loading ? (
            <div className="loading">Loading employees...</div>
          ) : (
            <table className="employees-table">
              <thead>
                <tr>
                  <th>EMPLOYEE NAME</th>
                  <th>COMPANY</th>
                  <th>ROLE</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {employees.length > 0 ? (
                  employees.map((employee) => (
                    <tr key={employee.id}>
                      <td>{employee.fullName || `${employee.firstName} ${employee.lastName}`}</td>
                      <td>{employee.company?.name || companyName}</td>
                      <td>{employee.role?.shortCode || 'N/A'}</td>
                      <td>
                        <button
                          className="btn-view-statement"
                          onClick={() => handleViewStatement(employee)}
                        >
                          View Statement
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">
                      No employees available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TourStatementsEmployeeList;